import { useEffect, useMemo, useState } from "react";
import CampaignIcon from "@mui/icons-material/Campaign";
import GroupsIcon from "@mui/icons-material/Groups";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PublicIcon from "@mui/icons-material/Public";
import CheckIcon from "@mui/icons-material/Check";
import { cls } from "../../hatim/soft/SoftKit";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useSendNotificationMutation } from "../../../api/notificationApi";
import { useGetManagedGroupsQuery } from "../../../api/managementApi";
import { useGetManagedHatimTemplatesQuery } from "../../../api/hatimManagementApi";
import {
  buildTargetOptions,
  buildSendPayloads,
  canSubmit,
  TITLE_MAX_LENGTH,
  BODY_MAX_LENGTH,
} from "../../../lib/notificationOptions";

const KIND_ICON = { all: PublicIcon, group: GroupsIcon, hatim: MenuBookIcon };

/** Seçilebilir hedef kutucuğu. */
function TargetChip({ option, active, onToggle, t }) {
  const Icon = KIND_ICON[option.kind] || GroupsIcon;
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onClick={() => onToggle(option.value)}
      className={[
        "inline-flex items-center gap-2 pl-3 pr-3.5 py-2 rounded-xl border text-sm transition-all",
        active
          ? "bg-terakkiPrimaryDark border-terakkiPrimaryDark text-white dark:bg-white dark:border-white dark:text-terakkiPrimaryDark"
          : "bg-terakkiSurface border-terakkiBorder text-terakkiPrimaryDark hover:border-terakkiPrimaryLight hover:bg-terakkiBrandCloud dark:bg-white/[0.04] dark:border-white/[0.14] dark:text-[#8FC9CA] dark:hover:bg-white/[0.08]",
      ].join(" ")}
    >
      {active ? (
        <CheckIcon sx={{ fontSize: 18 }} aria-hidden />
      ) : (
        <Icon sx={{ fontSize: 18 }} aria-hidden />
      )}
      <span className="font-SemiBold">{option.labelKey ? t(option.labelKey) : option.label}</span>
    </button>
  );
}

/** Aynı türdeki hedefleri başlık altında toplayan blok. */
function TargetGroup({ titleKey, options, selected, onToggle, t }) {
  if (options.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-SemiBold uppercase tracking-wide text-terakkiNeutral dark:text-white/70">
        {t(titleKey)}
      </span>
      <div className="flex flex-wrap gap-2" role="listbox" aria-multiselectable="true">
        {options.map((o) => (
          <TargetChip
            key={o.value}
            option={o}
            active={selected.includes(o.value)}
            onToggle={onToggle}
            t={t}
          />
        ))}
      </div>
    </div>
  );
}

export default function SendNotificationForm({ onSent }) {
  const { t } = useScriptMode();
  const { user } = useAuth();
  const [selected, setSelected] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const [okMessage, setOkMessage] = useState("");

  const { data: groupsResponse } = useGetManagedGroupsQuery();
  const { data: templatesResponse } = useGetManagedHatimTemplatesQuery();
  const [send, { isLoading }] = useSendNotificationMutation();

  const options = useMemo(
    () =>
      buildTargetOptions({
        role: user?.role,
        groups: groupsResponse?.data?.managedGroups,
        templates: templatesResponse?.data?.templates,
      }),
    [user?.role, groupsResponse, templatesResponse]
  );

  // Seçili bir hedef listeden düşerse (silinme, yenilenme, rol değişimi)
  // seçimi sessizce bırakmak yerine temizliyoruz; aksi hâlde buton etkin
  // görünüp hiçbir şey göndermez.
  useEffect(() => {
    setSelected((prev) => {
      const valid = prev.filter((v) => options.some((o) => o.value === v));
      return valid.length === prev.length ? prev : valid;
    });
  }, [options]);

  const toggle = (value) =>
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );

  const submitEnabled = canSubmit({ selected, title, body, isLoading });

  const handleSubmit = async () => {
    if (!submitEnabled) return;
    const payloads = buildSendPayloads(selected, options);
    if (payloads.length === 0) return;

    setError("");
    setOkMessage("");

    // Sunucu tek kitle alıyor; her hedefe ayrı istek gidiyor.
    // Biri başarısız olsa da diğerleri gönderilsin diye tek tek değerlendiriyoruz.
    const results = await Promise.allSettled(
      payloads.map((p) => send({ ...p, title: title.trim(), body: body.trim() }).unwrap())
    );
    const failed = results.filter((r) => r.status === "rejected");
    const sentCount = results.length - failed.length;

    if (sentCount > 0) {
      setTitle("");
      setBody("");
      setSelected([]);
      setOkMessage(`${sentCount} ${t("notif_send_count")}`);
      onSent?.();
    }
    if (failed.length > 0) {
      const reasons = Array.from(
        new Set(failed.map((f) => f.reason?.message || t("notif_send_error")))
      );
      setError(`${t("notif_send_partial")} ${reasons.join(" · ")}`);
    }
  };

  const inputClass =
    "w-full rounded-xl border border-terakkiBorder bg-terakkiSurface px-3.5 py-3 text-sm text-terakkiInk placeholder:text-terakkiNeutral focus:outline-none focus:border-terakkiPrimary focus:ring-2 focus:ring-terakkiPrimaryLight/40 transition-all dark:bg-white/10 dark:border-white/[0.14] dark:text-white dark:placeholder:text-white/45";

  const groupOptions = options.filter((o) => o.kind === "group" || o.kind === "all");
  const hatimOptions = options.filter((o) => o.kind === "hatim");

  return (
    <div className={`${cls.panel} overflow-hidden`}>
      <div className="flex items-center gap-2.5 px-4 py-3 bg-terakkiBrandCloud border-b border-terakkiBrandMist dark:bg-white/[0.06] dark:border-white/[0.14]">
        <span className="w-9 h-9 rounded-full bg-terakkiBrandCloud text-terakkiPrimaryDark flex items-center justify-center shrink-0 dark:bg-white/10 dark:text-[#8FC9CA]">
          <CampaignIcon aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-lg font-SemiBold text-terakkiPrimaryDark leading-tight dark:text-white">
            {t("notif_send_heading")}
          </div>
          <div className="text-xs text-terakkiNeutral leading-tight dark:text-white/70">
            {selected.length > 0
              ? `${selected.length} ${t("notif_target_selected")}`
              : t("notif_target_none")}
          </div>
        </div>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => setSelected([])}
            className="text-xs font-SemiBold text-terakkiNeutral hover:text-terakkiPrimaryDark transition-colors shrink-0 dark:text-white/70"
          >
            {t("notif_target_clear")}
          </button>
        )}
      </div>

      <div className="p-4 flex flex-col gap-5">
        <div className="flex flex-col gap-3">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-SemiBold text-terakkiPrimaryDark dark:text-[#8FC9CA]">
              {t("notif_target_label")}
            </span>
            <span className="text-xs text-terakkiNeutral dark:text-white/70">{t("notif_target_hint")}</span>
          </div>

          {options.length === 0 ? (
            <p className="text-sm text-terakkiNeutral py-2 dark:text-white/70">{t("notif_target_empty")}</p>
          ) : (
            <div className="flex flex-col gap-4">
              <TargetGroup
                titleKey="notif_target_groups"
                options={groupOptions}
                selected={selected}
                onToggle={toggle}
                t={t}
              />
              <TargetGroup
                titleKey="notif_target_hatims"
                options={hatimOptions}
                selected={selected}
                onToggle={toggle}
                t={t}
              />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="notif-title"
            className="text-sm font-SemiBold text-terakkiPrimaryDark dark:text-[#8FC9CA]"
          >
            {t("notif_title_label")}
          </label>
          <input
            id="notif-title"
            type="text"
            value={title}
            maxLength={TITLE_MAX_LENGTH}
            onChange={(e) => setTitle(e.target.value)}
            className={inputClass}
          />
          <span className="text-[11px] text-terakkiNeutral self-end dark:text-white/70">
            {title.length}/{TITLE_MAX_LENGTH}
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="notif-body" className="text-sm font-SemiBold text-terakkiPrimaryDark dark:text-[#8FC9CA]">
            {t("notif_body_label")}
          </label>
          <textarea
            id="notif-body"
            rows={4}
            value={body}
            maxLength={BODY_MAX_LENGTH}
            onChange={(e) => setBody(e.target.value)}
            className={`${inputClass} resize-y min-h-[104px]`}
          />
          <span className="text-[11px] text-terakkiNeutral self-end dark:text-white/70">
            {body.length}/{BODY_MAX_LENGTH}
          </span>
        </div>

        {error && (
          <p className="text-sm text-terakkiError bg-terakkiError/10 border border-terakkiError/20 rounded-xl px-3.5 py-2.5 dark:text-[#F2A59C]">
            {error}
          </p>
        )}
        {okMessage && (
          <p className="text-sm text-terakkiSuccess bg-terakkiSuccess/10 border border-terakkiSuccess/20 rounded-xl px-3.5 py-2.5 dark:text-[#7BE3A8]">
            {okMessage}
          </p>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!submitEnabled}
          className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-terakkiPrimaryDark text-white text-sm font-SemiBold hover:bg-[#00565C] dark:hover:bg-[#E6F4F4] transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-terakkiPrimaryDark dark:bg-white dark:text-terakkiPrimaryDark"
        >
          <CampaignIcon fontSize="small" aria-hidden />
          {isLoading ? t("notif_sending") : t("notif_submit")}
        </button>
      </div>
    </div>
  );
}
